/**
 * Breakdown sort transform.
 *
 * Reorders the series array (stack order, legend, render order) by name
 * or total value. Client-side only — does not modify SQL.
 * Applies to bar/line/area with >1 series.
 */
import type { EChartsOption } from 'echarts';
import type { ChartStyleState } from '../../../../edit/visualConfig';
import { getCapabilities } from '../../../../edit/visualConfig';
import { isSingleObject } from './helpers';

export function applyBreakdownSort(option: EChartsOption, vc: ChartStyleState, chartType?: string): EChartsOption {
  if (!vc.breakdownSort || vc.breakdownSort === 'none') return option;
  if (!Array.isArray(option.series) || (option.series as any[]).length <= 1) return option;

  // Breakdown sort only applies to axis-based charts with multiple series
  const caps = getCapabilities((chartType ?? 'bar') as any);
  if (!caps.axis) return option;

  const result = { ...option };
  const sorted = [...(result.series as any[])];

  const sumOf = (s: any): number =>
    Array.isArray(s.data) ? s.data.reduce((acc: number, v: any) => acc + (Number(v) || 0), 0) : 0;

  switch (vc.breakdownSort) {
    case 'name_asc':
      sorted.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
      break;
    case 'name_desc':
      sorted.sort((a, b) => String(b.name ?? '').localeCompare(String(a.name ?? '')));
      break;
    case 'value_desc':
      sorted.sort((a, b) => sumOf(b) - sumOf(a));
      break;
    case 'value_asc':
      sorted.sort((a, b) => sumOf(a) - sumOf(b));
      break;
  }

  result.series = sorted;

  // Keep legend order in sync with the new series order
  if (isSingleObject(result.legend)) {
    (result.legend as any).data = sorted.map((s: any) => s.name).filter(Boolean);
  }

  return result;
}